import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faViber } from '@fortawesome/free-brands-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import ChangeLog from './ChangeLog';
import useScrollLock from '../../hooks/useScrollLock';
import './Salon.css';

const AddEditAppointmentModal = ({ 
  isOpen, 
  onClose, 
  salonId, 
  onAppointmentAdded, 
  appointment, 
  mode = 'add',
  onNavigateToReservations,
  onOpenReservationModal,
  onOpenLinkedReservation,
  userRole
}) => {
  const { t, formatWeddingDate } = useLanguage();
  const { salonSettings } = useSalon();

  const isEditLocked = useMemo(() => {
    if (mode !== 'edit') return false;
    if (userRole === 'owner' || userRole === 'admin') return false;
    const apptAt = appointment?.appointment_datetime || appointment?.appointment_datetime_formatted;
    if (!apptAt) return false;
    const lockDays = salonSettings?.reservation_edit_lock_days?.value ?? 30;
    const diffMs = Date.now() - new Date(apptAt).getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays > lockDays;
  }, [mode, userRole, appointment, salonSettings]);

  const [formData, setFormData] = useState({
    appointment_datetime: '',
    employee_id: '',
    name: '',
    surname: '',
    wedding_date: '',
    dress_pickup_date: '',
    dress_return_date: '',
    phone_number: '',
    instagram_handle: '',
    notes: '',
    reservation_id: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isUnavailableModalOpen, setIsUnavailableModalOpen] = useState(false);
  const [unavailableDresses, setUnavailableDresses] = useState([]);
  const [unavailableLoading, setUnavailableLoading] = useState(false);
  const modalBodyRef = useRef(null);
  const originalAppointmentRef = useRef(null);
  const [isChangeLogVisible, setIsChangeLogVisible] = useState(false);
  const shouldScrollToFirstErrorRef = useRef(false);
  const [interestedDresses, setInterestedDresses] = useState([]);
  const [dressSearchTerm, setDressSearchTerm] = useState('');
  const [dressSearchResults, setDressSearchResults] = useState([]);
  const [dressSearchLoading, setDressSearchLoading] = useState(false);
  const dressSearchRef = useRef(null);
  const dressSearchTimeoutRef = useRef(null);
  const interestedDressesFetchIdRef = useRef(0);

  // Linked reservation state
  const [linkedReservation, setLinkedReservation] = useState(null);
  const [reservationSearchTerm, setReservationSearchTerm] = useState('');
  const [reservationSearchResults, setReservationSearchResults] = useState([]);
  const [reservationSearchLoading, setReservationSearchLoading] = useState(false);
  const [showReservationSearchResults, setShowReservationSearchResults] = useState(false);
  const reservationSearchTimeoutRef = useRef(null);

  const canManageLinkedReservation = userRole === 'owner' || userRole === 'admin';

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Scroll to first error field when form is submitted with errors
  useEffect(() => {
    if (shouldScrollToFirstErrorRef.current && Object.keys(errors).length > 0 && modalBodyRef.current) {
      // Order of fields in the form (to find first error)
      const fieldOrder = ['appointment_datetime', 'employee_id', 'name', 'surname', 'instagram_handle'];
      
      // Find the first field with an error
      const firstErrorField = fieldOrder.find(field => errors[field]);
      
      if (firstErrorField) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the field to make it even more obvious
            errorElement.focus();
          }
          // Reset the flag after scrolling
          shouldScrollToFirstErrorRef.current = false;
        }, 100);
      } else {
        // If no specific field error found, reset flag
        shouldScrollToFirstErrorRef.current = false;
      }
    } else if (error && modalBodyRef.current && !shouldScrollToFirstErrorRef.current) {
      // If there's a general error (not field-specific), scroll to top
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, errors]);

  // Filter employees based on current user role
  const getFilteredEmployees = useCallback((allEmployees, currentUserRole) => {
    if (!currentUserRole || !allEmployees) return [];
    
    switch (currentUserRole) {
      case 'owner':
        // Owner can assign appointments to anybody
        return allEmployees;
      
      case 'admin':
        // Admin cannot assign to owners, only to other admins and members
        return allEmployees.filter(emp => emp.role !== 'owner');
      
      case 'member':
        // Members can only assign to other members
        return allEmployees.filter(emp => emp.role === 'member');
      
      default:
        return [];
    }
  }, []);

  // Fetch all salon employees (without filtering)
  const fetchAllEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allEmployees = response.data.members || [];
      setEmployees(allEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError(t('messages.error.failedToFetchSalonEmployees'));
    } finally {
      setLoadingEmployees(false);
    }
  }, [salonId, t]);

  // Fetch employees when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAllEmployees();
    }
  }, [isOpen, fetchAllEmployees]);

  // Clear dress search timeout when modal closes
  useEffect(() => {
    if (!isOpen && dressSearchTimeoutRef.current) {
      clearTimeout(dressSearchTimeoutRef.current);
      dressSearchTimeoutRef.current = null;
    }
  }, [isOpen]);

  const fetchDressSearch = useCallback(async (searchTerm) => {
    const term = (searchTerm != null ? searchTerm : dressSearchTerm || '').toString().trim();
    if (!term) {
      setDressSearchResults([]);
      return;
    }
    try {
      setDressSearchLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses`, {
        params: { search: term, limit: 20, page: 1 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data?.dresses || [];
      setDressSearchResults(list.map(d => ({ id: d.id, name: d.name, brand_name: d.brand_name || null })));
    } catch (err) {
      console.error('Error searching dresses:', err);
      setDressSearchResults([]);
    } finally {
      setDressSearchLoading(false);
    }
  }, [salonId, dressSearchTerm]);

  const handleDressSearchChange = useCallback((e) => {
    const value = e.target.value;
    setDressSearchTerm(value);
    if (dressSearchTimeoutRef.current) {
      clearTimeout(dressSearchTimeoutRef.current);
      dressSearchTimeoutRef.current = null;
    }
    if (value.trim().length >= 2) {
      dressSearchTimeoutRef.current = setTimeout(() => {
        fetchDressSearch(value);
      }, 300);
    } else {
      setDressSearchResults([]);
    }
  }, [fetchDressSearch]);

  const addInterestedDress = useCallback((dress) => {
    setInterestedDresses(prev => {
      if (prev.some(d => d.id === dress.id)) return prev;
      return [...prev, { id: dress.id, name: dress.name, brand_name: dress.brand_name || null }];
    });
    setDressSearchTerm('');
    setDressSearchResults([]);
  }, []);

  const removeInterestedDress = useCallback((dressId) => {
    setInterestedDresses(prev => prev.filter(d => d.id !== dressId));
  }, []);

  // Clean up reservation search timeout on close
  useEffect(() => {
    if (!isOpen && reservationSearchTimeoutRef.current) {
      clearTimeout(reservationSearchTimeoutRef.current);
      reservationSearchTimeoutRef.current = null;
    }
  }, [isOpen]);

  const fetchReservationSearch = useCallback(async (searchTerm) => {
    const term = (searchTerm != null ? searchTerm : reservationSearchTerm || '').toString().trim();
    if (!term) {
      setReservationSearchResults([]);
      return;
    }
    try {
      setReservationSearchLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/reservations`, {
        params: { search: term },
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data?.reservations || [];
      setReservationSearchResults(list.map(r => ({
        id: r.id,
        bride_name: r.bride_name,
        bride_surname: r.bride_surname,
        wedding_date: r.wedding_date,
        wedding_date_formatted: r.wedding_date_formatted || '',
        dress_name: r.dress_name || ''
      })));
    } catch (err) {
      console.error('Error searching reservations:', err);
      setReservationSearchResults([]);
    } finally {
      setReservationSearchLoading(false);
    }
  }, [salonId, reservationSearchTerm]);

  const handleReservationSearchChange = useCallback((e) => {
    const value = e.target.value;
    setReservationSearchTerm(value);
    setShowReservationSearchResults(true);
    if (reservationSearchTimeoutRef.current) {
      clearTimeout(reservationSearchTimeoutRef.current);
      reservationSearchTimeoutRef.current = null;
    }
    if (value.trim().length >= 2) {
      reservationSearchTimeoutRef.current = setTimeout(() => {
        fetchReservationSearch(value);
      }, 300);
    } else {
      setReservationSearchResults([]);
    }
  }, [fetchReservationSearch]);

  const handleLinkReservation = useCallback((reservation) => {
    setFormData(prev => ({ ...prev, reservation_id: reservation.id }));
    setLinkedReservation({
      id: reservation.id,
      bride_name: reservation.bride_name,
      bride_surname: reservation.bride_surname,
      wedding_date: reservation.wedding_date,
      wedding_date_formatted: reservation.wedding_date_formatted || '',
      dress_name: reservation.dress_name || ''
    });
    setReservationSearchTerm('');
    setReservationSearchResults([]);
    setShowReservationSearchResults(false);
  }, []);

  const handleUnlinkReservation = useCallback(() => {
    setFormData(prev => ({ ...prev, reservation_id: null }));
    setLinkedReservation(null);
  }, []);

  const handleViewLinkedReservation = useCallback(() => {
    if (!formData.reservation_id || !onOpenLinkedReservation) return;
    onClose();
    onOpenLinkedReservation(formData.reservation_id);
  }, [formData.reservation_id, onOpenLinkedReservation, onClose]);

  // Filter employees based on current user role
  const filteredEmployees = useMemo(() => {
    return getFilteredEmployees(employees, userRole);
  }, [employees, userRole, getFilteredEmployees]);

  // Convert database field name to localized label for appointments
  const getLocalizedFieldName = (fieldName) => {
    const fieldNameMap = {
      'appointment_datetime': 'appointmentDateTime',
      'employee_id': 'assignedEmployee',
      'name': 'firstName',
      'surname': 'lastName',
      'wedding_date': 'weddingDate',
      'dress_pickup_date': 'dressPickupDate',
      'dress_return_date': 'dressReturnDate',
      'phone_number': 'phoneNumber',
      'instagram_handle': 'instagramHandle',
      'notes': 'notes',
      'reservation_id': 'linkedReservation'
    };

    const translationKey = fieldNameMap[fieldName];
    if (translationKey) {
      return t(`appointments.${translationKey}`);
    }
    
    // Fallback: convert snake_case to Title Case
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Populate form data when editing or modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset change log visibility when modal opens
      setIsChangeLogVisible(false);
      // Clear errors when modal opens
      setErrors({});
      setError('');
      // Reset scroll flag when modal opens
      shouldScrollToFirstErrorRef.current = false;
      setDressSearchTerm('');
      setDressSearchResults([]);
      setReservationSearchTerm('');
      setReservationSearchResults([]);
      setShowReservationSearchResults(false);
      setLinkedReservation(null);

      if (mode === 'edit' && appointment) {
        const reservationIdValue = appointment.reservation_id != null ? appointment.reservation_id : null;
        const appointmentData = {
          appointment_datetime: appointment.appointment_datetime_formatted || '',
          employee_id: appointment.employee_id || '',
          name: appointment.name || '',
          surname: appointment.surname || '',
          wedding_date: appointment.wedding_date_formatted || '',
          dress_pickup_date: appointment.dress_pickup_date_formatted || '',
          dress_return_date: appointment.dress_return_date_formatted || '',
          phone_number: appointment.phone_number || '',
          instagram_handle: appointment.instagram_handle || '',
          notes: appointment.notes || '',
          reservation_id: reservationIdValue
        };
        setFormData(appointmentData);
        originalAppointmentRef.current = appointmentData;

        // Set linked reservation summary if already present on the appointment prop
        if (reservationIdValue && appointment.linked_reservation_bride_name) {
          setLinkedReservation({
            id: reservationIdValue,
            bride_name: appointment.linked_reservation_bride_name,
            bride_surname: appointment.linked_reservation_bride_surname || '',
            wedding_date: appointment.linked_reservation_wedding_date || null,
            wedding_date_formatted: appointment.linked_reservation_wedding_date_formatted || '',
            dress_name: appointment.linked_reservation_dress_name || ''
          });
        }

        // Fetch the full appointment when we need extra data (interested dresses or linked reservation summary)
        const hasInterestedDresses = appointment.interested_dresses && Array.isArray(appointment.interested_dresses);
        const needsLinkedReservationFetch = !!reservationIdValue && !appointment.linked_reservation_bride_name;
        if (hasInterestedDresses) {
          setInterestedDresses(appointment.interested_dresses.map(d => ({ id: d.id, name: d.name, brand_name: d.brand_name || null })));
        }
        if (appointment.id && (!hasInterestedDresses || needsLinkedReservationFetch)) {
          const fetchId = ++interestedDressesFetchIdRef.current;
          const token = localStorage.getItem('token');
          axios.get(`/api/salons/${salonId}/appointments/${appointment.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (fetchId !== interestedDressesFetchIdRef.current) return;
            const fetched = res.data?.appointment;
            if (!fetched) return;
            if (!hasInterestedDresses) {
              const list = fetched.interested_dresses;
              setInterestedDresses(Array.isArray(list) ? list.map(d => ({ id: d.id, name: d.name, brand_name: d.brand_name || null })) : []);
            }
            if (fetched.reservation_id && fetched.linked_reservation_bride_name) {
              setLinkedReservation({
                id: fetched.reservation_id,
                bride_name: fetched.linked_reservation_bride_name,
                bride_surname: fetched.linked_reservation_bride_surname || '',
                wedding_date: fetched.linked_reservation_wedding_date || null,
                wedding_date_formatted: fetched.linked_reservation_wedding_date_formatted || '',
                dress_name: fetched.linked_reservation_dress_name || ''
              });
            } else if (!fetched.reservation_id) {
              setLinkedReservation(null);
            }
          }).catch(() => {
            if (fetchId !== interestedDressesFetchIdRef.current) return;
            if (!hasInterestedDresses) setInterestedDresses([]);
          });
        } else if (!hasInterestedDresses) {
          setInterestedDresses([]);
        }
      } else {
        setInterestedDresses([]);
        // Reset form for add mode
        setFormData({
          appointment_datetime: '',
          employee_id: '',
          name: '',
          surname: '',
          wedding_date: '',
          dress_pickup_date: '',
          dress_return_date: '',
          phone_number: '',
          instagram_handle: '',
          notes: '',
          reservation_id: null
        });
      }
      setError('');
    }
  }, [isOpen, mode, appointment, salonId]);

  // Function to calculate pickup and return dates based on wedding date
  const calculateDressDates = (weddingDate) => {
    if (!weddingDate) {
      return { pickupDate: '', returnDate: '' };
    }

    const wedding = new Date(weddingDate);
    
    // Calculate pickup date (2 days before wedding)
    const pickupDate = new Date(wedding);
    pickupDate.setDate(wedding.getDate() - 2);
    
    // If pickup date is Sunday, move to Saturday
    if (pickupDate.getDay() === 0) { // Sunday
      pickupDate.setDate(pickupDate.getDate() - 1); // Move to Saturday
    }
    
    // Calculate return date (2 days after wedding)
    const returnDate = new Date(wedding);
    returnDate.setDate(wedding.getDate() + 2);
    
    // If return date is Sunday, move to Monday
    if (returnDate.getDay() === 0) { // Sunday
      returnDate.setDate(returnDate.getDate() + 1); // Move to Monday
    }
    
    // Format dates for input fields (YYYY-MM-DD)
    const formatDateForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      pickupDate: formatDateForInput(pickupDate),
      returnDate: formatDateForInput(returnDate)
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset scroll flag when user starts typing (they're fixing errors)
    shouldScrollToFirstErrorRef.current = false;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };
      
      // If wedding date is being changed, automatically calculate pickup and return dates
      if (name === 'wedding_date') {
        const { pickupDate, returnDate } = calculateDressDates(value);
        newFormData.dress_pickup_date = pickupDate;
        newFormData.dress_return_date = returnDate;
      }
      
      return newFormData;
    });
  };

  const handleCheckUnavailableDresses = async () => {
    if (!formData.dress_pickup_date || !formData.dress_return_date) {
      setError(t('messages.error.selectPickupAndReturnDates'));
      return;
    }

    setUnavailableLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/appointments/unavailable-dresses`, {
        params: {
          appointment_pickup_date: formData.dress_pickup_date,
          appointment_return_date: formData.dress_return_date
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setUnavailableDresses(response.data.unavailable_dresses || []);
      setIsUnavailableModalOpen(true);
    } catch (error) {
      console.error('Error fetching unavailable dresses:', error);
      setError(error.response?.data?.error || t('messages.error.failedToFetchUnavailableDresses'));
    } finally {
      setUnavailableLoading(false);
    }
  };

  const handleMakeReservation = () => {
    if (!onOpenReservationModal) {
      alert('Reservation modal not available.');
      return;
    }

    // Prepare reservation data from appointment
    const reservationData = {
      mode: "add",
      bride_name: formData.name,
      bride_surname: formData.surname,
      wedding_date: formData.wedding_date,
      dress_pickup_datetime: formData.dress_pickup_date ? `${formData.dress_pickup_date}T13:00` : '',
      dress_return_datetime: formData.dress_return_date ? `${formData.dress_return_date}T15:00` : '',
      phone_number: formData.phone_number,
      instagram_handle: formData.instagram_handle,
      reservation_date: new Date().toISOString().split('T')[0], // Today
      contract_date: new Date().toISOString().split('T')[0], // Today
      appointment_id: appointment?.id || null
    };

    console.log('Making reservation with data:', reservationData);
    console.log('Current formData:', formData);

    // Close the appointment modal
    onClose();
    
    // Open reservation modal with pre-filled data
    onOpenReservationModal(reservationData);
  };

  const logAppointmentChanges = async (originalAppointment, updatedData) => {
    if (!originalAppointment || !appointment?.id) {
      console.log('logAppointmentChanges: Skipping - no originalAppointment or appointment.id');
      return;
    }

    console.log('logAppointmentChanges: Starting', { 
      appointmentId: appointment.id,
      originalKeys: Object.keys(originalAppointment),
      updatedKeys: Object.keys(updatedData)
    });

    const trackedFields = [
      'appointment_datetime',
      'employee_id',
      'name',
      'surname',
      'wedding_date',
      'dress_pickup_date',
      'dress_return_date',
      'phone_number',
      'instagram_handle',
      'notes',
      'reservation_id'
    ];

    const sanitizeValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') return String(value);
      return String(value).trim();
    };

    const changes = trackedFields.reduce((acc, field) => {
      const previousValue = sanitizeValue(originalAppointment[field]);
      const newValue = sanitizeValue(updatedData[field]);
      if (previousValue !== newValue) {
        acc.push({
          field_name: field,
          previous_value: previousValue || null,
          new_value: newValue || null
        });
      }
      return acc;
    }, []);

    console.log('logAppointmentChanges: Found changes', changes);

    if (changes.length === 0) {
      console.log('logAppointmentChanges: No changes detected');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('logAppointmentChanges: No token found');
      return;
    }

    try {
      const results = await Promise.all(
        changes.map(change =>
          axios.post(
            `/api/salons/${salonId}/change-logs`,
            {
              entity_type: 'appointment',
              entity_id: appointment.id,
              field_name: change.field_name,
              previous_value: change.previous_value,
              new_value: change.new_value,
              change_type: 'update',
              metadata: {
                appointment_id: appointment.id
              }
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          ).then(response => {
            console.log('logAppointmentChanges: Successfully logged change', {
              field: change.field_name,
              response: response.data
            });
            return response;
          }).catch(error => {
            console.error('Error logging appointment change:', {
              field: change.field_name,
              error: error.response?.data || error.message,
              status: error.response?.status
            });
            throw error;
          })
        )
      );
      console.log('logAppointmentChanges: All changes logged successfully', results);
    } catch (error) {
      console.error('logAppointmentChanges: Error logging changes', error);
    }

    originalAppointmentRef.current = {
      ...originalAppointment,
      ...updatedData
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.appointment_datetime) {
      newErrors.appointment_datetime = t('messages.error.appointmentDateTimeRequired');
    }
    
    if (!formData.employee_id) {
      newErrors.employee_id = t('messages.error.employeeSelectionRequired');
    }
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('messages.error.firstNameRequired');
    }
    
    if (!formData.surname || !formData.surname.trim()) {
      newErrors.surname = t('messages.error.lastNameRequired');
    }
    
    if (!formData.instagram_handle || !formData.instagram_handle.trim()) {
      newErrors.instagram_handle = t('messages.error.instagramHandleRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrors({});

    // Validate required fields
    if (!validateForm()) {
      // Set flag to scroll to first error on next render
      shouldScrollToFirstErrorRef.current = true;
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = mode === 'add' 
        ? `/api/salons/${salonId}/appointments`
        : `/api/salons/${salonId}/appointments/${appointment.id}`;

      const method = mode === 'add' ? 'post' : 'put';
      const payload = { ...formData, interested_dress_ids: interestedDresses.map(d => d.id) };

      const response = await axios[method](url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        if (mode === 'edit') {
          await logAppointmentChanges(
            originalAppointmentRef.current || {
              appointment_datetime: appointment.appointment_datetime_formatted || '',
              employee_id: appointment.employee_id || '',
              name: appointment.name || '',
              surname: appointment.surname || '',
              wedding_date: appointment.wedding_date_formatted || '',
              dress_pickup_date: appointment.dress_pickup_date_formatted || '',
              dress_return_date: appointment.dress_return_date_formatted || '',
              phone_number: appointment.phone_number || '',
              instagram_handle: appointment.instagram_handle || '',
              notes: appointment.notes || '',
              reservation_id: appointment.reservation_id != null ? appointment.reservation_id : null
            },
            formData
          );
        }
        onAppointmentAdded();
        onClose();
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError(error.response?.data?.error || t('messages.error.failedToSaveAppointment'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChangeLog = () => {
    if (!appointment?.id) return;
    setIsChangeLogVisible(!isChangeLogVisible);
  };

  const handleDeleteClick = () => {
    if (window.confirm(t('messages.confirm.deleteAppointment'))) {
      handleDeleteAppointment();
    }
  };

  const handleDeleteAppointment = async () => {
    if (!appointment || !appointment.id) {
      setError('No appointment selected for deletion');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/salons/${salonId}/appointments/${appointment.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        onAppointmentAdded(); // Refresh the appointments list
        onClose();
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setError(error.response?.data?.error || t('messages.error.failedToDeleteAppointment'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{mode === 'add' ? t('appointments.addAppointment') : t('appointments.title')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body" ref={modalBodyRef}>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isEditLocked && (
            <div className="info-banner" style={{ background: 'var(--warning-bg, #fff3cd)', color: 'var(--warning-text, #856404)', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid var(--warning-border, #ffc107)' }}>
              {t('appointments.editLockedMessage')}
            </div>
          )}

          <fieldset disabled={isEditLocked} style={{ border: 'none', padding: 0, margin: 0 }}>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="appointment_datetime">{t('appointments.appointmentDateTime')} *</label>
              <div className="date-field-wrap">
                <input
                  type="datetime-local"
                  id="appointment_datetime"
                  name="appointment_datetime"
                  value={formData.appointment_datetime}
                  onChange={handleInputChange}
                  className={errors.appointment_datetime ? 'error' : ''}
                  required
                />
              </div>
              {errors.appointment_datetime && <span className="error-message">{errors.appointment_datetime}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="employee_id">{t('appointments.assignedEmployee')} *</label>
              {loadingEmployees ? (
                <div className="loading-message">{t('common.loading')}...</div>
              ) : (
                <select
                  id="employee_id"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleInputChange}
                  className={errors.employee_id ? 'error' : ''}
                  required
                >
                  <option value="">{t('appointments.selectEmployee')}...</option>
                  {filteredEmployees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name} ({employee.role})
                    </option>
                  ))}
                </select>
              )}
              {errors.employee_id && <span className="error-message">{errors.employee_id}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">{t('appointments.firstName')} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
                required
                maxLength="100"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="surname">{t('appointments.lastName')} *</label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                className={errors.surname ? 'error' : ''}
                required
                maxLength="100"
              />
              {errors.surname && <span className="error-message">{errors.surname}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="wedding_date">{t('appointments.weddingDate')}</label>
              <div className="date-field-wrap">
                <input
                  type="date"
                  id="wedding_date"
                  name="wedding_date"
                  value={formData.wedding_date}
                  onChange={handleInputChange}
                />
              </div>
              <small className="form-help-text">
                {t('appointments.autoCalculatedHelp')}
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dress_pickup_date">
                {t('appointments.dressPickupDate')}
                <span className="auto-calculated"> ({t('appointments.autoCalculated')})</span>
              </label>
              <div className="date-field-wrap">
                <input
                  type="date"
                  id="dress_pickup_date"
                  name="dress_pickup_date"
                  value={formData.dress_pickup_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="dress_return_date">
                {t('appointments.dressReturnDate')}
                <span className="auto-calculated"> ({t('appointments.autoCalculated')})</span>
              </label>
              <div className="date-field-wrap">
                <input
                  type="date"
                  id="dress_return_date"
                  name="dress_return_date"
                  value={formData.dress_return_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <button 
                type="button" 
                className="salon-button secondary"
                onClick={handleCheckUnavailableDresses}
                disabled={!formData.dress_pickup_date || !formData.dress_return_date}
              >
                {t('appointments.checkUnavailableDresses')}
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone_number">{t('appointments.phoneNumber')}</label>
              <div className="input-with-icon">
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  maxLength="20"
                  placeholder="+1234567890"
                />
                {formData.phone_number && (
                  <button
                    type="button"
                    className="contact-icon phone-icon"
                    onClick={() => window.open(`tel:${formData.phone_number}`, '_self')}
                    title={t('appointments.callClient')}
                  >
                    📞
                  </button>
                )}
                {formData.phone_number && (
                  <button
                    type="button"
                    className="contact-icon viber-icon"
                    onClick={() => {
                      const viberUrl = `viber://chat?number=${formData.phone_number}`;
                      window.open(viberUrl, '_self');
                    }}
                    title={t('appointments.openViberChat')}
                  >
                    <FontAwesomeIcon icon={faViber} />
                  </button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="instagram_handle">{t('appointments.instagram')} *</label>
              <div className="input-with-icon">
                <input
                  type="text"
                  id="instagram_handle"
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleInputChange}
                  className={errors.instagram_handle ? 'error' : ''}
                  maxLength="100"
                  placeholder={t('appointments.instagramPlaceholder')}
                  required
                />
                {formData.instagram_handle && (
                  <button
                    type="button"
                    className="contact-icon instagram-icon"
                    onClick={() => {
                      const instagramUrl = formData.instagram_handle.startsWith('@') 
                        ? `https://instagram.com/${formData.instagram_handle.substring(1)}`
                        : `https://instagram.com/${formData.instagram_handle}`;
                      window.open(instagramUrl, '_blank');
                    }}
                    title={t('appointments.openInstagram')}
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </button>
                )}
              </div>
              {errors.instagram_handle && <span className="error-message">{errors.instagram_handle}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="notes">{t('common.notes')}</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder={t('reservations.generalNotesPlaceholder')}
                rows="3"
                maxLength="1000"
              />
            </div>
          </div>

          {/* Dresses bride was interested in */}
          <div className="form-section" ref={dressSearchRef}>
            <label>{t('appointments.interestedDresses')}</label>
            <div className="interested-dresses-search">
              <input
                type="text"
                value={dressSearchTerm}
                onChange={handleDressSearchChange}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), fetchDressSearch())}
                placeholder={t('appointments.searchDressesPlaceholder')}
                className="form-control"
              />
              <button
                type="button"
                className="sleek-button"
                onClick={() => fetchDressSearch()}
                disabled={dressSearchLoading}
              >
                {dressSearchLoading ? t('common.loading') : t('appointments.addInterestedDress')}
              </button>
            </div>
            {dressSearchResults.length > 0 && (
              <ul className="interested-dresses-results">
                {dressSearchResults.map((dress) => (
                  <li key={dress.id}>
                    <button
                      type="button"
                      onClick={() => addInterestedDress(dress)}
                      disabled={interestedDresses.some(d => d.id === dress.id)}
                    >
                      {dress.name}{dress.brand_name ? ` (${dress.brand_name})` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {dressSearchTerm.trim() && dressSearchResults.length === 0 && !dressSearchLoading && (
              <p className="interested-dresses-no-results">{t('appointments.noDressesFound')}</p>
            )}
            {interestedDresses.length > 0 && (
              <ul className="interested-dresses-list">
                {interestedDresses.map((dress) => (
                  <li key={dress.id} className="interested-dress-chip">
                    <span>{dress.name}{dress.brand_name ? ` (${dress.brand_name})` : ''}</span>
                    <button
                      type="button"
                      className="remove-chip"
                      onClick={() => removeInterestedDress(dress.id)}
                      title={t('common.remove')}
                      aria-label={t('common.remove')}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Linked Reservation (admins/owners only) */}
          {canManageLinkedReservation && (
            <div className="form-section">
              <label>{t('appointments.linkedReservation')}</label>
              {linkedReservation ? (
                <div className="linked-reservation-card">
                  <div className="linked-reservation-info">
                    <div className="linked-reservation-name">
                      {linkedReservation.bride_name} {linkedReservation.bride_surname}
                    </div>
                    {linkedReservation.dress_name && (
                      <div className="linked-reservation-dress">{linkedReservation.dress_name}</div>
                    )}
                    {linkedReservation.wedding_date && (
                      <div className="linked-reservation-wedding-date">
                        {t('appointments.weddingDate')}: {formatWeddingDate(linkedReservation.wedding_date)}
                      </div>
                    )}
                  </div>
                  <div className="linked-reservation-actions">
                    <button
                      type="button"
                      className="sleek-button"
                      onClick={handleUnlinkReservation}
                    >
                      {t('appointments.unlinkReservation')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="linked-reservation-search">
                  <input
                    type="text"
                    value={reservationSearchTerm}
                    onChange={handleReservationSearchChange}
                    onFocus={() => setShowReservationSearchResults(true)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), fetchReservationSearch())}
                    placeholder={t('appointments.searchReservationPlaceholder')}
                    className="form-control"
                  />
                  {showReservationSearchResults && reservationSearchResults.length > 0 && (
                    <ul className="linked-reservation-results">
                      {reservationSearchResults.map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => handleLinkReservation(r)}
                          >
                            <span className="linked-reservation-result-name">
                              {r.bride_name} {r.bride_surname}
                            </span>
                            {r.dress_name && (
                              <span className="linked-reservation-result-dress"> — {r.dress_name}</span>
                            )}
                            {r.wedding_date && (
                              <span className="linked-reservation-result-date"> ({formatWeddingDate(r.wedding_date)})</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {showReservationSearchResults && reservationSearchTerm.trim().length >= 2 && !reservationSearchLoading && reservationSearchResults.length === 0 && (
                    <p className="linked-reservation-no-results">{t('appointments.noReservationsFound')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          </fieldset>

          {/* Delete Appointment Button and Change Log Button */}
          {mode === 'edit' && (
            <div className="form-section" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--border-color, #e0e0e0)' }}>
              <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="sleek-button change-log"
                  onClick={handleToggleChangeLog}
                  disabled={loading}
                >
                  {isChangeLogVisible ? t('reservations.hideChangeLog') : t('reservations.viewChangeLog')}
                </button>
                <button 
                  type="button" 
                  className="sleek-button delete"
                  onClick={handleDeleteClick}
                  disabled={loading || isEditLocked}
                >
                  {t('appointments.deleteAppointment')}
                </button>
              </div>
            </div>
          )}

          {/* Change Log Section - Only in edit mode */}
          {mode === 'edit' && (
            <ChangeLog
              salonId={salonId}
              entityType="appointment"
              entityId={appointment?.id}
              getLocalizedFieldName={getLocalizedFieldName}
              isVisible={isChangeLogVisible}
              onToggle={handleToggleChangeLog}
              scrollContainerRef={modalBodyRef}
              titleKey="reservations.changeLogTitle"
              namespace="reservations"
            />
          )}
        </form>

        <div className="modal-footer">
          <button 
            type="button" 
            className="salon-button secondary" 
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          {mode === 'edit' && formData.reservation_id && onOpenLinkedReservation && (
            <button 
              type="button" 
              className="salon-button tertiary" 
              onClick={handleViewLinkedReservation}
              disabled={loading}
            >
              {t('appointments.viewReservation')}
            </button>
          )}
          {mode === 'edit' && !formData.reservation_id && formData.name && formData.surname && (
            <button 
              type="button" 
              className="salon-button tertiary" 
              onClick={handleMakeReservation}
              disabled={loading || isEditLocked}
            >
              {t('appointments.createReservation')}
            </button>
          )}
          <button 
            type="submit" 
            className="salon-button primary" 
            onClick={handleSubmit}
            disabled={loading || isEditLocked}
          >
            {loading ? `${t('common.save')}...` : (mode === 'add' ? t('appointments.addAppointment') : t('common.saveChanges'))}
          </button>
        </div>
      </div>

      {/* Unavailable Dresses Modal */}
      {isUnavailableModalOpen && (
        <div className="modal-overlay">
          <div className="modal unavailable-dresses-modal">
            <div className="modal-header">
              <h2>{t('appointments.unavailableDresses')}</h2>
              <button className="modal-close" onClick={() => setIsUnavailableModalOpen(false)}>×</button>
            </div>

            <div className="modal-body">
              {unavailableLoading ? (
                <div className="loading-message">
                  <div className="loading-spinner"></div>
                  {t('appointments.checkingUnavailable')}
                </div>
              ) : (
                <>
                  {unavailableDresses.length === 0 ? (
                    <div className="success-message">
                      ✅ {t('appointments.allDressesAvailable')}
                    </div>
                  ) : (
                    <div className="unavailable-dresses-list">
                      <p className="unavailable-count">
                        {unavailableDresses.length} {t('appointments.dressesUnavailable')}:
                      </p>
                      {unavailableDresses.map((dress, index) => (
                        <div key={index} className="unavailable-dress-item">
                          <div className="dress-info" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', padding: '15px' }}>
                              <div className="dress-name">{dress.dress_name}</div>
                              <div className="dress-brand">{dress.brand_name}</div>
                              <div className="reserved-by">
                                {t('appointments.reservedBy')}: {dress.reserved_by}
                              </div>
                            </div>
                            <div className="reservation-dates">
                              <div className="reservation-pickup">
                                {t('appointments.pickup')}: {new Date(dress.reservation_pickup).toLocaleDateString()}
                              </div>
                              <div className="reservation-return">
                                {t('appointments.return')}: {new Date(dress.reservation_return).toLocaleDateString()}
                              </div>
                              <div className="wedding-date">
                                {t('appointments.weddingDate')}: {new Date(dress.wedding_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="salon-button primary" 
                onClick={() => setIsUnavailableModalOpen(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEditAppointmentModal;
